import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import type { Preset } from '@/constants/presets';

import Collapsible from './Collapsible';
import {
  exampleInput,
  exampleOptions,
  type TemplateExample,
  useTemplateTranslation,
} from './kit';
import RichText from './RichText';

/**
 * More real results, input next to output. The first example already sits
 * in the workspace, so this section shows the rest.
 */
export default function TemplateExamples({
  preset,
  examples,
}: {
  preset: Preset;
  examples: TemplateExample[];
}) {
  const { t, lang } = useTemplateTranslation();
  const shown = examples.filter((example) => example.completion);
  if (!shown.length) return null;

  const title = t(preset.title) || preset.title;
  const shortTitle = title.replace(/\s*Generator\s*$/i, '').trim() || title;

  return (
    <section
      id='examples'
      className='mx-auto max-w-6xl px-4 pt-24 sm:px-6 sm:pt-32 lg:px-8'
    >
      <SectionHeading
        size='md'
        title={
          lang === 'en' ? `${shortTitle} examples.` : t('Generated text by AI')
        }
        tail={lang === 'en' ? 'Real results, made with this tool.' : undefined}
      />
      <ul
        className={clsxm(
          'mt-12 grid items-start gap-4',
          shown.length > 1 && 'lg:grid-cols-2',
        )}
      >
        {shown.map((example) => {
          const input = exampleInput(example, preset.fields);
          const options = exampleOptions(example, preset.fields);
          return (
            <li
              key={example.id}
              className={clsxm(
                'flex flex-col overflow-hidden rounded-[22px]',
                tone.surface,
                tone.line,
              )}
            >
              {input || options.length ? (
                <div className='border-b border-[#0f0f28]/[0.06] bg-[#f9f9fc] px-6 py-5 dark:border-white/[0.06] dark:bg-white/[0.02]'>
                  <p
                    className={clsxm(
                      'text-[12px] font-semibold uppercase tracking-[0.08em]',
                      tone.quiet,
                    )}
                  >
                    {t('INPUT')}
                  </p>
                  {input ? (
                    <p
                      className={clsxm(
                        'mt-1.5 line-clamp-3 whitespace-pre-line text-[15px] font-medium leading-6',
                        tone.ink,
                      )}
                    >
                      {input}
                    </p>
                  ) : null}
                  {options.length ? (
                    <ul className='mt-3 flex flex-wrap gap-1.5'>
                      {options.map((option) => (
                        <li
                          key={option}
                          className={clsxm(
                            'rounded-full bg-white px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                            tone.body,
                          )}
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
              <div className='flex-1 px-6 py-5'>
                <p className='text-[12px] font-semibold uppercase tracking-[0.08em] text-primary-700 dark:text-primary-300'>
                  {t('OUTPUT')}
                </p>
                <div className='mt-3'>
                  <Collapsible maxHeightClass='max-h-[20rem]'>
                    <RichText text={example.completion || ''} />
                  </Collapsible>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
